/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { v4 as genUuid } from "uuid";
import { API, ItemType, Item, APIResultList } from "@thenounproject/lingo-core";
import { InsertType } from "@constants/InsertType";
import createAsyncAction from "../actionCreators/createAsyncAction";
import { SnakeCasedProperties } from "type-fest";

export type NewItem = {
  uuid?: string;
  type: ItemType;
  kit_uuid: string;
  section_uuid?: string;
  item_uuid?: string;
  display_order?: string | number;
  [key: string]: any;
};

export type InsertConfig = {
  newItems: NewItem[];
  sectionId?: string;
  itemId?: string;
  insertIndex: number;
  targetIndex: number;
  guidePosition?: string;
  dragPosition?: string;
  insertType: InsertType;
};

export type DisplayProperties = Partial<SnakeCasedProperties<Item["displayProperties"]>>;

export type ItemUpdate = {
  uuid: string;
  section_uuid?: string;
  display_order?: string;
  data?: {
    display_size?: number;
    background_color?: string;
    display_style?: Item["data"]["displayStyle"];
    title?: Item["data"]["title"];
    color?: Item["data"]["color"];
  };
  display_properties?: DisplayProperties;
};

/**
 * Batch updates an array of items, optionally create new item(s)
 * @param {Array} updatedItems Array of items updates
 * @param {Number} insertConfig Object containing sectionId, newItems, insertIndex, insertType, guidePosition, dragPosition
 */

type Args = {
  updatedItems: ItemUpdate[];
  insertConfig?: InsertConfig;
};

// Maps snake_case display_properties keys to camelCase Item.displayProperties keys
const dpKeyMap: Record<string, string> = {
  size: "size",
  show_metadata: "showMetadata",
  allow_download: "allowDownload",
  allow_preview: "allowPreview",
  caption: "caption",
  card_background_color: "cardBackgroundColor",
  autoplay: "autoplay",
  image_alignment: "imageAlignment",
  display_style: "displayStyle",
};

// Maps snake_case data keys to camelCase Item.data keys
const dataKeyMap: Record<string, string> = {
  display_size: "displaySize",
  background_color: "backgroundColor",
  display_style: "displayStyle",
  title: "title",
  color: "color",
};

const [useBatchSaveItems, batchSaveItems] = createAsyncAction(
  {
    action: "items/batchSave",
    undo: {
      message: (args: Args) =>
        args.updatedItems.length > 0 ? `Updated ${args.updatedItems.length} item(s)` : null,
      condition: (args: Args) => args.updatedItems.length > 0 && !args.insertConfig,
      captureState: (args: Args, { getState }) => {
        const items = getState().entities.items.objects;
        return args.updatedItems.map(update => {
          const item = items[`${update.uuid}-0`];
          if (!item) return { uuid: update.uuid };
          const reverseUpdate: ItemUpdate = { uuid: update.uuid };
          if (update.data) {
            reverseUpdate.data = {} as ItemUpdate["data"];
            for (const key of Object.keys(update.data)) {
              const camelKey = dataKeyMap[key] ?? key;
              (reverseUpdate.data as any)[key] = (item.data as any)?.[camelKey];
            }
          }
          if (update.display_properties) {
            reverseUpdate.display_properties = {} as DisplayProperties;
            for (const key of Object.keys(update.display_properties)) {
              const camelKey = dpKeyMap[key] ?? key;
              (reverseUpdate.display_properties as any)[key] = (item.displayProperties as any)?.[
                camelKey
              ];
            }
          }
          return reverseUpdate;
        });
      },
      performUndo: (_args: Args, captured: unknown, { dispatch }) => {
        void dispatch(batchSaveItems({ updatedItems: captured as ItemUpdate[] }));
      },
    },
  },
  async (args: Args, { getState }) => {
    const { updatedItems, insertConfig } = args;
    const { newItems = [], insertIndex = null } = insertConfig ?? {};
    const mutationUpdates = insertConfig?.newItems ?? [];

    // Track source sections for items being moved so the reducer can update query caches
    const sectionMoves: Record<string, string> = {};
    const itemObjects = getState().entities.items.objects;
    updatedItems.forEach(u => {
      if (u.section_uuid) {
        const item = itemObjects[`${u.uuid}-0`];
        if (item?.sectionUuid && item.sectionUuid !== u.section_uuid) {
          sectionMoves[u.uuid] = item.sectionUuid;
        }
      }
    });

    /**
     * If there is more than one new item, overwrite the display_order
     * to be after the previous item in the array.
     */
    const orderedNewItems = newItems.map((currentItem, idx) => {
      if (idx > 0) {
        currentItem.display_order = `after:${newItems[idx - 1].uuid}`;
      }
      currentItem.uuid = (currentItem.uuid || (genUuid() as string)).toUpperCase();
      return currentItem;
    });

    const res = await API.call<APIResultList<"items", string>>({
      endpoint: "sync/items",
      method: "POST",
      data: {
        items: [...updatedItems, ...mutationUpdates],
      },
    });

    return {
      newItems: orderedNewItems.map(({ uuid, type }) => {
        return { type, uuid };
      }),
      insertIndex,
      sectionMoves,
      result: res.result.items,
      entities: res.entities,
    };
  }
);

export default useBatchSaveItems;
export { batchSaveItems };
